/**
 * LinkWellModal — Phase 24 / Task 24.3 (DEC-094) + Task 24.6 (DEC-097).
 *
 * Server-backed well search constrained to the battery's platform operator
 * (hard block — the server also enforces operator match). Picking a well links
 * it to the battery (UNIQUE tenant+well → "already linked" surfaced as a toast).
 *
 * Task 24.6 — a well may carry an active standalone (well-direct) tank. Such
 * wells show a warning icon; picking one opens a confirm prompt before linking,
 * because the link deactivates the standalone tank (server requires the explicit
 * deactivateStandaloneTank flag, DISPATCH_MANAGER+).
 */
import * as React from 'react';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { CatalogSearchModal } from './CatalogSearchModal';
import { useLinkWell, useWellSearch, type WellResult } from './hooks';
import { subText } from './ui-bits';
import { formatNumber } from '@lodex-pro/shared/number-format';

export interface LinkWellModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly batteryId: string;
  readonly batteryName: string;
  /** The battery operator's platform operator id — results are constrained to it. */
  readonly operatorId: string;
}

export function LinkWellModal({
  open,
  onOpenChange,
  batteryId,
  batteryName,
  operatorId,
}: LinkWellModalProps): React.JSX.Element {
  const link = useLinkWell(batteryId);
  // A well with a standalone tank pending confirmation.
  const [pending, setPending] = React.useState<WellResult | null>(null);

  const useSearch = (q: string): { items: readonly WellResult[]; isFetching: boolean } => {
    // operatorId is a stable prop → hook order is stable across renders.
    const { data, isFetching } = useWellSearch(q, operatorId);
    return { items: data?.wells ?? [], isFetching };
  };

  const doLink = (w: WellResult, deactivateStandaloneTank: boolean): void => {
    link.mutate(
      { wellId: w.id, ...(deactivateStandaloneTank ? { deactivateStandaloneTank: true } : {}) },
      {
        onSuccess: () => {
          toast.success(`Linked ${w.wellName}`);
          setPending(null);
          onOpenChange(false);
        },
        onError: () => toast.error('Could not link well — it may already be linked.'),
      },
    );
  };

  return (
    <>
      <CatalogSearchModal<WellResult>
        open={open}
        onOpenChange={onOpenChange}
        title="Link well"
        placeholder="Search wells by name…"
        useSearch={useSearch}
        itemKey={(w): string => w.id}
        renderItem={(w): React.ReactNode => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {w.standaloneTank !== null && (
              <AlertTriangle
                size={14}
                aria-label="This well has a standalone tank"
                style={{ color: 'var(--color-warning)', flexShrink: 0 }}
              />
            )}
            <span>
              <span style={{ fontWeight: 600 }}>{w.wellName}</span>
              <span style={subText}>
                {` · API ${w.apiNumber}`}
                {w.county != null ? ` · ${w.county}` : ''}
                {` · ${w.state}`}
                {w.standaloneTank !== null ? ' · has standalone tank' : ''}
              </span>
            </span>
          </span>
        )}
        onPick={(w): void => {
          if (w.standaloneTank !== null) {
            // Confirm before deactivating the standalone tank.
            onOpenChange(false);
            setPending(w);
          } else {
            doLink(w, false);
          }
        }}
      />

      {/* Standalone-tank confirm prompt (Task 24.6). */}
      <Dialog
        open={pending !== null}
        onOpenChange={(o): void => {
          if (!o) setPending(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>This well has a standalone tank</DialogTitle>
          </DialogHeader>
          {pending?.standaloneTank != null && (
            <p style={{ fontSize: '14px', lineHeight: 1.5 }}>
              {pending.wellName} has a standalone tank{' '}
              <strong>
                {pending.standaloneTank.name}
                {pending.standaloneTank.capacityBbl != null
                  ? `, ${formatNumber(pending.standaloneTank.capacityBbl)} bbl`
                  : ''}
              </strong>
              . Adding it to <strong>{batteryName}</strong> will deactivate the standalone tank.
              Continue?
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={(): void => setPending(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={link.isPending}
              onClick={(): void => {
                if (pending) doLink(pending, true);
              }}
              style={{
                backgroundColor: 'var(--color-accent-blue)',
                color: 'var(--color-on-accent)',
                borderColor: 'var(--color-accent-blue)',
              }}
            >
              {link.isPending ? 'Working…' : 'Deactivate standalone tank and add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
